import { useState } from 'react';
import {
  Button,
  H2,
  Input,
  Paragraph,
  Spinner,
  Text,
  XStack,
  YStack,
} from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { ListItem } from '@mrrmlab/shared-types';
import {
  useAddListItem,
  useDeleteListItem,
  useList,
  useUpdateListItem,
} from '../hooks';

type Priority = 'low' | 'normal' | 'high';

const PRIORITIES: readonly { value: Priority; label: string }[] = [
  { value: 'low', label: 'Niedrig' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Hoch' },
];

const PRIORITY_LABEL: Record<Priority, string> = {
  low: 'Niedrig',
  normal: 'Normal',
  high: 'Hoch',
};

/** Accept YYYY-MM-DD or full ISO; anything else passes through for API validation. */
function toIso(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T00:00:00.000Z`;
  return t;
}

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export interface TodoListScreenProps {
  listId: string;
  onBack?: () => void;
}

export function TodoListScreen({ listId, onBack }: TodoListScreenProps) {
  const query = useList(listId);
  const addItem = useAddListItem(listId);
  const updateItem = useUpdateListItem(listId);
  const deleteItem = useDeleteListItem(listId);

  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<Priority>('normal');

  const canSubmit = title.trim().length > 0;

  const submit = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    const dueDateIso = toIso(dueDate);
    addItem.mutate(
      {
        data: {
          type: 'todo',
          title: trimmedTitle,
          done: false,
          ...(dueDateIso !== null ? { dueDate: dueDateIso } : {}),
          priority,
        },
      },
      {
        onSuccess: () => {
          setTitle('');
          setDueDate('');
          setPriority('normal');
        },
      },
    );
  };

  if (query.isPending) {
    return (
      <Screen>
        <Spinner />
      </Screen>
    );
  }

  if (query.isError) {
    return (
      <Screen>
        <Paragraph color="$red10">Liste konnte nicht geladen werden: {query.error.message}</Paragraph>
        {onBack ? <Button onPress={onBack}>← Zurück</Button> : null}
      </Screen>
    );
  }

  const list = query.data;
  if (list.type !== 'todo') {
    return (
      <Screen>
        <Paragraph color="$red10">
          This screen only handles `todo` lists, but this list is `{list.type}`.
        </Paragraph>
        {onBack ? <Button onPress={onBack}>← Zurück</Button> : null}
      </Screen>
    );
  }

  const sortedItems = [...list.items].sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.createdAt.localeCompare(b.createdAt);
  });

  return (
    <Screen scrollable>
      <XStack alignItems="center" gap="$3">
        {onBack ? (
          <Button size="$2" chromeless onPress={onBack}>
            ← Zurück
          </Button>
        ) : null}
        <H2 flex={1}>{list.title}</H2>
      </XStack>

      <YStack gap="$2">
        <Input
          placeholder="Aufgabentitel"
          value={title}
          onChangeText={setTitle}
          onSubmitEditing={submit}
        />
        <Input
          placeholder="Fälligkeitsdatum (JJJJ-MM-TT, optional)"
          value={dueDate}
          onChangeText={setDueDate}
        />
        <XStack gap="$2">
          {PRIORITIES.map((p) => (
            <Button
              key={p.value}
              size="$2"
              theme={priority === p.value ? 'active' : undefined}
              onPress={() => setPriority(p.value)}
            >
              {p.label}
            </Button>
          ))}
        </XStack>
        <Button disabled={addItem.isPending || !canSubmit} onPress={submit}>
          Aufgabe hinzufügen
        </Button>
      </YStack>

      {sortedItems.length === 0 ? (
        <Paragraph opacity={0.7}>Noch keine Aufgaben.</Paragraph>
      ) : (
        <YStack gap="$2">
          {sortedItems.map((item) => (
            <TodoItemCard
              key={item.id}
              item={item}
              onSave={(data) =>
                updateItem.mutate({ itemId: item.id, dto: { data } })
              }
              onDelete={() => deleteItem.mutate(item.id)}
              busy={updateItem.isPending || deleteItem.isPending}
            />
          ))}
        </YStack>
      )}
    </Screen>
  );
}

interface TodoItemCardProps {
  item: ListItem;
  onSave: (data: {
    type: 'todo';
    title: string;
    done: boolean;
    dueDate?: string | null;
    priority?: Priority;
  }) => void;
  onDelete: () => void;
  busy: boolean;
}

function TodoItemCard({ item, onSave, onDelete, busy }: TodoItemCardProps) {
  const todo = item.data.type === 'todo' ? item.data : null;
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editPriority, setEditPriority] = useState<Priority>('normal');

  if (!todo) return null;

  const startEdit = () => {
    setEditTitle(todo.title);
    setEditDueDate(toDateInput(todo.dueDate));
    setEditPriority(todo.priority ?? 'normal');
    setEditing(true);
  };

  const save = () => {
    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle) return;
    const dueDateIso = toIso(editDueDate);
    onSave({
      type: 'todo',
      title: trimmedTitle,
      done: todo.done,
      dueDate: dueDateIso,
      priority: editPriority,
    });
    setEditing(false);
  };

  const toggleDone = () => {
    onSave({
      type: 'todo',
      title: todo.title,
      done: !todo.done,
      dueDate: todo.dueDate ?? null,
      priority: todo.priority,
    });
  };

  return (
    <YStack
      gap="$2"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$background"
    >
      {editing ? (
        <>
          <Input value={editTitle} onChangeText={setEditTitle} placeholder="Titel" />
          <Input
            value={editDueDate}
            onChangeText={setEditDueDate}
            placeholder="Fälligkeitsdatum (JJJJ-MM-TT, optional)"
          />
          <XStack gap="$2">
            {PRIORITIES.map((p) => (
              <Button
                key={p.value}
                size="$2"
                theme={editPriority === p.value ? 'active' : undefined}
                onPress={() => setEditPriority(p.value)}
              >
                {p.label}
              </Button>
            ))}
          </XStack>
          <XStack gap="$2" justifyContent="flex-end">
            <Button size="$2" chromeless onPress={() => setEditing(false)}>
              Abbrechen
            </Button>
            <Button size="$2" disabled={busy || editTitle.trim().length === 0} onPress={save}>
              Speichern
            </Button>
          </XStack>
        </>
      ) : (
        <XStack alignItems="center" gap="$2">
          <Button size="$2" chromeless disabled={busy} onPress={toggleDone}>
            {todo.done ? '☑' : '☐'}
          </Button>
          <YStack flex={1} gap="$1">
            <Text
              fontSize="$5"
              fontWeight="600"
              textDecorationLine={todo.done ? 'line-through' : 'none'}
              opacity={todo.done ? 0.5 : 1}
            >
              {todo.title}
            </Text>
            <XStack gap="$2" alignItems="center">
              {todo.priority && todo.priority !== 'normal' ? (
                <PriorityBadge priority={todo.priority} />
              ) : null}
              {todo.dueDate ? (
                <Text fontSize="$2" opacity={0.6}>
                  Fällig {toDateInput(todo.dueDate)}
                </Text>
              ) : null}
            </XStack>
          </YStack>
          <Button size="$2" chromeless onPress={startEdit}>
            Bearbeiten
          </Button>
          <Button size="$2" chromeless disabled={busy} onPress={onDelete}>
            ✕
          </Button>
        </XStack>
      )}
    </YStack>
  );
}

function PriorityBadge({ priority }: { priority: Priority }) {
  const colors: Record<Priority, string> = {
    low: '$color5',
    normal: '$color7',
    high: '$red9',
  };
  return (
    <XStack
      paddingHorizontal="$2"
      paddingVertical="$1"
      borderRadius="$2"
      backgroundColor={colors[priority]}
    >
      <Text fontSize="$1" textTransform="uppercase" letterSpacing={0.5} color="white">
        {PRIORITY_LABEL[priority]}
      </Text>
    </XStack>
  );
}
